import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { UserModel } from 'shared/models';
import { UiControlPropsType } from 'shared/types';
import { useAsyncEffect, useDebounce, useMedia, useObservable, useOnClickOutside } from 'shared/hooks';
import { usersQuery } from 'shared/queries';
import { COLORS } from 'shared/constants';

import { UiIcon } from '../UiIcon';
import { UiLoading } from '../UiLoading';
import { UiAvatar } from '../UiAvatar';
import { UiScrollbar } from '../UiScrollbar';
import { UiModal } from '../UiModal';
import { UiModalContent } from '../UiModalContent';

import './styles.scss';

type SinglePropsType = {
    scrollIntoView?: boolean;
    multiple?: false;
    preventReSelection?: boolean;
    isReadOnly?: boolean;
} & UiControlPropsType<number | null>;

type MultiplePropsType = {
    scrollIntoView?: boolean;
    multiple: true;
    preventReSelection?: boolean;
    isReadOnly?: boolean;
} & UiControlPropsType<UserModel[]>;

type PropsType = SinglePropsType | MultiplePropsType;

export const UiUserSelect = observer(function UiUserSelect({ multiple, preventReSelection, ...props }: PropsType) {
    if (multiple) {
        return <UiUserSelectMultiple preventReSelection={preventReSelection} {...props as MultiplePropsType} />;
    }

    return <UiUserSelectSingle preventReSelection={preventReSelection} {...props as SinglePropsType} />;
});

export const UiUserSelectSingle = observer(function UiUserSelectSingle({
    value = null,
    onChange = () => undefined,
    name = 'ui-users-select',
    scrollIntoView = false,
    preventReSelection = false,
    isReadOnly = false,
}: SinglePropsType) {
    const store = useObservable({
        user: null as UserModel | null,
    });

    useAsyncEffect(async () => {
        if (value === null || +value === 0) {
            store.set('user', null);
            return;
        }
        if (value === store.user?.id) {
            return;
        }
        const response = await usersQuery({ id: value });
        if (response.isSuccess && response.data && response.data.items.length > 0) {
            store.set('user', new UserModel(response.data.items[0]));
        } else {
            store.set('user', null);
        }
    }, [value]);

    const placeholder = () => {
        if (value !== null && value !== 0 && store.user === null) {
            return 'Загрузка...';
        }
        if (store.user) {
            return 'Изменить';
        }
        return 'Выбрать';
    };

    return (
        <UiUserSelectCore
            closeOnAdd
            scrollIntoView={scrollIntoView}
            value={store.user ? [store.user] : []}
            placeholder={placeholder()}
            preventReSelection={preventReSelection}
            isReadOnly={isReadOnly}
            onAdd={(user) => {
                store.set('user', user);
                onChange({
                    name,
                    value: user.id,
                });
            }}
            onRemove={() => {
                onChange({
                    name,
                    value: null,
                });
            }}
        />
    );
});

export const UiUserSelectMultiple = observer(function UiUserSelectMultiple({
    value = [],
    onChange = () => undefined,
    name = 'ui-users-select',
    scrollIntoView = false,
    preventReSelection = false,
    isReadOnly = false,
}: MultiplePropsType) {
    return (
        <UiUserSelectCore
            scrollIntoView={scrollIntoView}
            value={value}
            preventReSelection={preventReSelection}
            isReadOnly={isReadOnly}
            onAdd={(user) => {
                onChange({
                    name,
                    value: [...value, user],
                });
            }}
            onRemove={(user) => {
                onChange({
                    name,
                    value: value.filter(u => u.id !== user.id),
                });
            }}
        />
    );
});


type CorePropsType = {
    value: UserModel[];
    onAdd?: (user: UserModel) => void;
    closeOnAdd?: boolean;
    onRemove?: (user: UserModel) => void;
    scrollIntoView?: boolean;
    placeholder?: string;
    preventReSelection?: boolean;
    isReadOnly?: boolean;
}

export const UiUserSelectCore = observer(function UiUserSelectMultiple({
    value = [],
    onAdd = () => undefined,
    closeOnAdd = false,
    onRemove = () => undefined,
    placeholder = 'Добавить',
    scrollIntoView = false,
    preventReSelection = false,
    isReadOnly = false,
}: CorePropsType) {
    const ref = useRef<HTMLDivElement>(null);
    const refOuter = useRef<HTMLDivElement>(null);
    const { isDesktop } = useMedia();
    const store = useObservable({
        isLoading: false,
        isOpened: false,
        users: [] as UserModel[],
        query: '',
    });

    useOnClickOutside(ref, () => {
        if (!isDesktop) {
            return;
        }
        store.update({
            isOpened: false,
            query: '',
        });
    });

    const search = useDebounce(async (query: string) => {
        // Если строка поиска пуста, не выполняем запрос
        if (!query.trim()) {
            store.set('users', []);
            store.set('isLoading', false);
            return;
        }

        const response = await usersQuery({ query, limit: 20 });
        if (response.isSuccess && response.data && store.query === query) {
            store.set('users', response.data.items.map(user => new UserModel(user)));
        }
        store.set('isLoading', false);

        setTimeout(() => refOuter?.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }, 500);

    const handleAdd = (user: UserModel) => {
        // Проверяем, не выбран ли пользователь уже, если включено предотвращение повторного выбора
        if (preventReSelection && value.some(selectedUser => selectedUser.id === user.id)) {
            return; // Не добавляем пользователя, если он уже выбран
        }

        onAdd(user);

        if (closeOnAdd || preventReSelection) {
            // Закрываем список и очищаем поле поиска
            store.update({
                isOpened: false,
                query: '',
            });
        }
    };

    const handleRemove = (user: UserModel) => {
        onRemove(user);
        // Не закрываем список и не очищаем пользователей при удалении,
        // чтобы пользователь мог продолжить выбор
        store.set('query', '');
    };

    const handleToggle = () => {
        if (isReadOnly) {
            return;
        }

        if (!store.isOpened) {
            // При открытии не очищаем пользователей, чтобы сохранить результаты поиска
            store.set('isOpened', true);
        } else {
            // При закрытии очищаем состояние
            store.update({
                isOpened: false,
                users: [],
                query: '',
            });
        }

        if (scrollIntoView) {
            setTimeout(() => {
                refOuter?.current?.scrollIntoView({
                    behavior: 'smooth',
                });
            }, 100);
        }
    };

    const outer = () => (
        <>
            <input
                placeholder={'Начните вводить имя сотрудника...'}
                value={store.query}
                autoFocus
                onChange={e => {
                    store.set('query', e.target.value || '');
                    if (!e.target.value) {
                        store.set('isLoading', false);
                        // Если поле пустое, возвращаем пустой список, чтобы пользователь мог явно нажать на иконку для открытия
                        store.set('users', []);
                        return;
                    }
                    store.set('isLoading', true);
                    search(e.target.value);
                }}
                className="ui-new-users-select-outer__query"
            />
            {store.isLoading && (
                <div className="ui-new-users-select-outer__loading">
                    <UiLoading />
                </div>
            )}
            {!store.isLoading && store.users.length > 0 && (
                <UiScrollbar maxHeight={250} className="ui-new-users-select-outer__items">
                    {store.users.map(user => {
                        const isAlreadySelected = value.some(selectedUser => selectedUser.id === user.id);
                        return (
                            <div
                                key={user.id}
                                className={classnames("ui-new-users-select-outer__user", {
                                    "ui-new-users-select-outer__user--selected": isAlreadySelected,
                                    "ui-new-users-select-outer__user--disabled": preventReSelection && isAlreadySelected
                                })}
                                onClick={preventReSelection && isAlreadySelected ? undefined : () => handleAdd(user)}
                            >
                                <UiAvatar size={24} label={user.previewName} image={user.imageAvatar} />
                                <span>{user.name}</span>
                                {preventReSelection && isAlreadySelected && (
                                    <UiIcon.Check size={20} className="ui-new-users-select-outer__user-check" />
                                )}
                            </div>
                        );
                    })}
                </UiScrollbar>
            )}
        </>
    );

    return (
        <div
            ref={ref}
            className={classnames('ui-new-users-select', {
                'ui-new-users-select--empty': value.length === 0,
                'ui-new-users-select--opened': store.isOpened,
                'ui-new-users-select--readonly': isReadOnly,
            })}
        >
            <div className="ui-new-users-select-control" onClick={handleToggle}>
                <div className="ui-new-users-select-control__inner">
                    {(value.length === 0 && !isReadOnly) && <UiIcon.UserRoundPlus color={COLORS._175CE5} size={20} />}
                    {value.map(user => (
                        <div key={user.id} className="ui-new-users-select-control-user">
                            <span>{user.previewName}</span>
                            {!isReadOnly && (
                                <UiIcon.X
                                    className={'ui-new-users-select-control-user__remove'}
                                    size={16}
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleRemove(user);
                                    }}
                                />
                            )}
                        </div>
                    ))}
                    {!isReadOnly && <div className="ui-new-users-select-control__placeholder">{placeholder}</div>}
                </div>
                <div className="ui-new-users-select-control__chevron">
                    <UiIcon.ChevronDown size={16} />
                </div>
            </div>
            {(isDesktop && store.isOpened) && (
                <div className="ui-new-users-select-outer" ref={refOuter}>
                    {outer()}
                </div>
            )}
            {!isDesktop && store.isOpened && (
                <UiModal isOpened={store.isOpened} onClose={() => store.set('isOpened', false)}>
                    <UiModalContent>
                        {outer()}
                    </UiModalContent>
                </UiModal>
            )}
        </div>
    );
});
